import { useMemo } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Droplets, Umbrella } from "lucide-react";

interface MinutelyPrecipProps {
  minutely: { dt: number; precipitation: number }[] | undefined;
}

export function MinutelyPrecip({ minutely }: MinutelyPrecipProps) {
  const analysis = useMemo(() => {
    if (!minutely || minutely.length === 0) return null;

    const maxPrecip = Math.max(...minutely.map((m) => m.precipitation));
    if (maxPrecip < 0.1) {
      return { type: "none" as const, message: "No precipitation expected in the next hour" };
    }

    let rainStart: number | null = null;
    let rainEnd: number | null = null;

    for (let i = 0; i < minutely.length; i++) {
      if (minutely[i].precipitation > 0) {
        if (rainStart === null) rainStart = i;
        rainEnd = i;
      }
    }

    if (rainStart === null) {
      return { type: "none" as const, message: "No precipitation expected in the next hour" };
    }

    const totalVolume = minutely.reduce((sum, m) => sum + m.precipitation, 0);
    const maxIntensity = maxPrecip;

    if (rainStart === 0) {
      const endMin = rainEnd! + 1;
      const endLabel = endMin < 60 ? `in ${endMin} min` : "within the hour";
      return {
        type: "raining" as const,
        message: `Rain now, ending ${endLabel}. ${totalVolume.toFixed(1)}mm total`,
        rainStart,
        rainEnd: rainEnd!,
        maxIntensity,
      };
    }

    const startLabel = rainStart < 60 ? `in ${rainStart} min` : "after an hour";
    return {
      type: "upcoming" as const,
      message: `Rain starting ${startLabel}. ${totalVolume.toFixed(1)}mm expected`,
      rainStart,
      rainEnd: rainEnd!,
      maxIntensity,
    };
  }, [minutely]);

  if (!analysis || analysis.type === "none") return null;

  const isUpcoming = analysis.type === "upcoming";

  return (
    <GlassPanel intensity="medium" className={`p-4 ${isUpcoming ? "!border-sky-500/20 bg-sky-500/5" : "!border-blue-500/20 bg-blue-500/5"}`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg shrink-0 ${isUpcoming ? "bg-sky-500/10" : "bg-blue-500/10"}`}>
          {isUpcoming ? (
            <Umbrella className="h-4.5 w-4.5 text-sky-400" />
          ) : (
            <Droplets className="h-4.5 w-4.5 text-blue-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold uppercase tracking-wider ${isUpcoming ? "text-sky-400" : "text-blue-400"}`}>
              {isUpcoming ? "Upcoming Rain" : "Rain Now"}
            </span>
          </div>
          <p className="text-sm text-foreground/85">{analysis.message}</p>
          {minutely && (
            <div className="mt-3 flex items-end gap-px h-10">
              {minutely.slice(0, 60).map((m, i) => {
                const barHeight = analysis.maxIntensity > 0
                  ? Math.max(2, (m.precipitation / analysis.maxIntensity) * 100)
                  : 0;
                const isActive = i >= (analysis.rainStart ?? 0) && i <= (analysis.rainEnd ?? 0);
                return (
                  <div
                    key={i}
                    className="flex-1 min-w-[2px] rounded-t-sm transition-colors"
                    style={{
                      height: `${barHeight}%`,
                      backgroundColor: isActive
                        ? m.precipitation > 2.5
                          ? "rgb(59 130 246)"
                          : m.precipitation > 0.5
                            ? "rgb(56 189 248)"
                            : "rgb(125 211 252)"
                        : "rgba(148 163 184, 0.15)",
                    }}
                  />
                );
              })}
            </div>
          )}
          <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
            <span>Now</span>
            <span>30m</span>
            <span>60m</span>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}